import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { api } from '@/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { 
  User, LogOut, CreditCard, Award, Search, 
  Mail, Calendar, Loader2, CheckCircle, Clock,
  TrendingUp, Gift, QrCode
} from 'lucide-react';

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { customer, logout, loading: authLoading } = useCustomerAuth();
  const [activeCards, setActiveCards] = useState([]);
  const [availableCards, setAvailableCards] = useState([]);
  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [actionsStatus, setActionsStatus] = useState('OPEN');
  const [actionsPage, setActionsPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activating, setActivating] = useState(null);
  const [progressPopup, setProgressPopup] = useState(null);
  const [selectedQrCard, setSelectedQrCard] = useState(null); // { userCardId, stampCardId }
  const [selectedActionQr, setSelectedActionQr] = useState(null); // { code, actionTitle, companyName, campaignName }
  const [qrPopupStatus, setQrPopupStatus] = useState(null); // { type, message, before, after, total }
  const [showQrSuccessAnimation, setShowQrSuccessAnimation] = useState(false);
  const [qrAnimFading, setQrAnimFading] = useState(false);
  const [qrNow, setQrNow] = useState(Date.now());
  const prevProgressRef = useRef(new Map());
  const qrPopupPrevRef = useRef(null);
  const qrBase = typeof window !== 'undefined' ? `${window.location.origin}/api/public/qr` : '/api/public/qr';
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  useEffect(() => {
    if (!authLoading && !customer) {
      navigate('/customer/login');
    }
  }, [authLoading, customer, navigate]);

  useEffect(() => {
    if (customer) {
      loadStampCards();
      loadActions();
    }
  }, [customer]);

  useEffect(() => {
    if (!progressPopup) return;
    const timeoutId = setTimeout(() => setProgressPopup(null), 7000);
    return () => clearTimeout(timeoutId);
  }, [progressPopup]);

  useEffect(() => {
    if (!selectedQrCard?.userCardId && !selectedQrCard?.stampCardId) return;
    if (showQrSuccessAnimation) return;
    setQrNow(Date.now());
    const secId = setInterval(() => setQrNow(Date.now()), 1000);
    const refreshId = setInterval(() => {
      loadStampCards(true);
    }, 5000);
    return () => {
      clearInterval(secId);
      clearInterval(refreshId);
    };
  }, [selectedQrCard?.userCardId, selectedQrCard?.stampCardId, showQrSuccessAnimation]);

  useEffect(() => {
    if (!showQrSuccessAnimation) return;
    setQrAnimFading(false);
    const fadeTimer = setTimeout(() => {
      setQrAnimFading(true);
    }, 6500);
    const timeoutId = setTimeout(() => {
      setShowQrSuccessAnimation(false);
      setQrAnimFading(false);
    }, 7000);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(timeoutId);
    };
  }, [showQrSuccessAnimation]);

  useEffect(() => {
    if (!selectedQrCard?.userCardId && !selectedQrCard?.stampCardId) return;
    const current =
      activeCards.find((x) => x.id === selectedQrCard?.userCardId) ||
      activeCards.find((x) => x?.stampCard?.id === selectedQrCard?.stampCardId) ||
      null;
    if (!current) return;

    const total = Number(current?.stampCard?.stampCount || 0);
    const progress = Number(current?.progress || 0);
    const redeemedAt = current?.redeemedAt || null;
    const prev = qrPopupPrevRef.current;
    if (prev) {
      if (progress > Number(prev.progress || 0)) {
        setQrPopupStatus({
          type: 'progress',
          message: 'Code eingescannt',
          before: Number(prev.progress || 0),
          after: progress,
          total,
        });
        setShowQrSuccessAnimation(true);
      } else if (!prev.redeemedAt && redeemedAt) {
        setQrPopupStatus({
          type: 'redeemed',
          message: 'Belohnung wurde eingelöst.',
          before: progress,
          after: progress,
          total,
        });
        setShowQrSuccessAnimation(true);
      }
    }
    qrPopupPrevRef.current = { progress, redeemedAt };
  }, [activeCards, selectedQrCard?.userCardId, selectedQrCard?.stampCardId]);

  async function loadActions() {
    setActionsLoading(true);
    try {
      const resp = await api.customerActions();
      if (resp?.ok && Array.isArray(resp.actions)) {
        setActions(resp.actions);
      } else {
        setActions([]);
      }
    } catch (error) {
      console.error('Failed to load customer actions:', error);
      setActions([]);
    } finally {
      setActionsLoading(false);
    }
  }

  async function loadStampCards(silent = false) {
    if (!silent) setLoading(true);
    try {
      const [active, available] = await Promise.all([
        api.customerStampCardsActive(),
        api.customerStampCardsAvailable(),
      ]);

      if (active?.ok && active?.stampCards) {
        const nextActive = active.stampCards;
        const prevMap = prevProgressRef.current;
        for (const userCard of nextActive) {
          const id = userCard?.id;
          if (!id) continue;
          const prev = Number(prevMap.get(id) ?? -1);
          const current = Number(userCard?.progress || 0);
          const total = Number(userCard?.stampCard?.stampCount || 0);
          if (prev >= 0 && current > prev) {
            setProgressPopup({
              name: userCard?.stampCard?.name || 'Stempelkarte',
              before: prev,
              after: current,
              total,
            });
          }
        }
        setActiveCards(nextActive);
        const rebuilt = new Map();
        for (const userCard of nextActive) {
          if (userCard?.id) rebuilt.set(userCard.id, Number(userCard?.progress || 0));
        }
        prevProgressRef.current = rebuilt;
      }

      if (available?.ok && available?.stampCards) {
        // Filter out already activated cards from available list
        const alreadyActivated = new Set(
          (active?.stampCards || []).map(c => c.stampCard?.id)
        );
        setAvailableCards(
          (available.stampCards || []).filter(c => !alreadyActivated.has(c.id) && !c.isActivated)
        );
      }
    } catch (error) {
      console.error('Failed to load stamp cards:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }

  async function handleActivateCard(cardId) {
    setActivating(cardId);
    try {
      const resp = await api.customerStampCardActivate(cardId);
      if (resp?.ok) {
        // Reload cards
        await loadStampCards();
      }
    } catch (error) {
      console.error('Failed to activate card:', error);
      alert('Aktivierung fehlgeschlagen: ' + (error.message || 'Unbekannter Fehler'));
    } finally {
      setActivating(null);
    }
  }

  async function handleLogout() {
    await logout();
    navigate('/customer/login');
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!customer) {
    return null;
  }

  const filteredAvailable = availableCards.filter(card =>
    card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    card.company?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openActions = actions.filter(a => a.status === 'OPEN');
  const expiredActions = actions.filter(a => a.status === 'EXPIRED');
  const redeemedActions = actions.filter(a => a.status === 'REDEEMED');

  const viewByStatus = {
    OPEN: { key: 'OPEN', title: 'Offen', variant: 'secondary', items: openActions },
    EXPIRED: { key: 'EXPIRED', title: 'Abgelaufen', variant: 'outline', items: expiredActions },
    REDEEMED: { key: 'REDEEMED', title: 'Eingelöst', variant: 'default', items: redeemedActions },
  };

  const selectedView = viewByStatus[actionsStatus] || viewByStatus.OPEN;
  const pageSize = 5;
  const totalPages = Math.max(1, Math.ceil((selectedView.items || []).length / pageSize));
  const safePage = Math.min(Math.max(actionsPage, 1), totalPages);
  const pagedItems = (selectedView.items || []).slice((safePage - 1) * pageSize, safePage * pageSize);

  function statusLabel(status) {
    if (status === 'REDEEMED') return 'eingelöst';
    if (status === 'EXPIRED') return 'abgelaufen';
    return 'offen';
  }

  function renderActionRow(a) {
    const requestedAt = a.requestedAt ? new Date(a.requestedAt) : null;
    const isOpenWithCode = a.status === 'OPEN' && a.type === 'REDEEM' && !!a.redeemCode;
    const expiredAtRaw = a.redeemEnd || a.requestEnd || a.campaignEndsAt || null;
    const expiredAt = expiredAtRaw ? new Date(expiredAtRaw) : null;
    const redeemedAt = a.redeemedAt ? new Date(a.redeemedAt) : null;
    return (
      <div
        key={`${a.type}-${a.actionId}-${a.requestedAt || ''}`}
        className={`flex items-start justify-between gap-4 py-3 border-b last:border-b-0 ${isOpenWithCode ? 'cursor-pointer hover:bg-blue-50 rounded px-2 -mx-2 transition-colors' : ''}`}
        onClick={() => {
          if (!isOpenWithCode) return;
          setSelectedActionQr({
            code: a.redeemCode,
            actionTitle: a.actionTitle,
            companyName: a.companyName,
            campaignName: a.campaignName,
          });
        }}
      >
        <div className="min-w-0">
          <div className="font-medium text-foreground truncate">
            {a.actionTitle || 'Aktion'}
          </div>
          <div className="text-sm text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <Mail className="h-4 w-4" />
              {a.type === 'PENDING_VERIFY' ? (
                <>
                  <span>Bestätigung ausstehend</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-border text-[10px] leading-none text-muted-foreground hover:bg-muted"
                        aria-label="Info: Code per E-Mail bestätigen"
                        onClick={(e) => e.stopPropagation()}
                      >
                        i
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      sideOffset={6}
                      className="w-64 text-xs"
                      onOpenAutoFocus={(e) => e.preventDefault()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Bitte bestätige den Code in deiner E-Mail. Erst danach kannst du ihn nutzen.
                    </PopoverContent>
                  </Popover>
                </>
              ) : (
                'Code angefordert'
              )}
            </span>
            {requestedAt && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {requestedAt.toLocaleDateString()}
              </span>
            )}
          </div>
          {(a.companyName || a.campaignName) && (
            <div className="text-sm text-muted-foreground mt-1">
              {[a.companyName, a.campaignName].filter(Boolean).join(' · ')}
            </div>
          )}
          {isOpenWithCode && (
            <div className="text-xs text-blue-700 mt-1 flex items-center gap-1">
              <QrCode className="h-3.5 w-3.5" />
              Tippen, um deinen QR-Code zu öffnen
            </div>
          )}
          {a.status === 'REDEEMED' && redeemedAt && (
            <div className="text-xs text-green-700 mt-1">
              Eingelöst am {redeemedAt.toLocaleDateString()} {redeemedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
          {a.status === 'EXPIRED' && (
            <div className="text-xs text-amber-700 mt-1">
              {expiredAt ? `Abgelaufen am ${expiredAt.toLocaleDateString()}` : 'Abgelaufen'}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={a.status === 'REDEEMED' ? 'default' : a.status === 'EXPIRED' ? 'outline' : 'secondary'}>
            {statusLabel(a.status)}
          </Badge>
        </div>
      </div>
    );
  }

  const customerInitials = customer
    ? ([customer.firstName, customer.lastName].filter(Boolean).map(s => s[0]).join('') || (customer.email || '?')[0]).toUpperCase()
    : '?';
  const customerDisplayName = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim() || customer.email || '';

  return (
    <div className="min-h-screen bg-muted">
      {/* Navbar */}
      <nav className="bg-background border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between" style={{ height: '52px' }}>
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 text-sm font-semibold text-foreground hover:opacity-80 transition-opacity">
            <img src="/favicon.svg" alt="Scanmirein" className="w-7 h-7" />
            <span className="tracking-tight">Scanmirein</span>
          </a>

          <div className="flex items-center gap-3">
            {/* Home icon */}
            <a
              href="/"
              title="Zur Landingpage"
              className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
                <path d="M9 21V12h6v9" />
              </svg>
            </a>

            {/* Avatar dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(o => !o)}
                className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-muted transition-colors"
              >
                <span className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center shrink-0 select-none">
                  {customerInitials}
                </span>
                <span className="text-xs font-medium text-foreground max-w-[140px] truncate hidden sm:block">
                  {customerDisplayName}
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-1.5 w-44 bg-background border border-border rounded-lg shadow-lg py-1 z-50">
                  <Link
                    to="/customer/profile"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <User className="w-4 h-4 text-muted-foreground" />
                    Mein Profil
                  </Link>
                  <div className="border-t border-border my-1" />
                  <button
                    onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Abmelden
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Mein Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Willkommen zurück, {customer.firstName || customer.email}</p>
        </div>
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Aktive Stempelkarten</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {activeCards.length}
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Verfügbare Karten</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {availableCards.length}
                  </p>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Gift className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Offene Aktionen</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {openActions.length}
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Eingelöste Aktionen</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {redeemedActions.length}
                  </p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Gesammelte Stempel</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {activeCards.reduce((sum, c) => sum + (c.progress || 0), 0)}
                  </p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Award className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* QR-requested Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-foreground mb-4">Meine Aktionen</h2>

          {actionsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : actions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Noch keine Aktionen vorhanden.</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Hier siehst du Aktionen, bei denen du einen QR-Code angefordert hast.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-lg">{selectedView.title}</CardTitle>
                  <Badge variant={selectedView.variant}>{selectedView.items.length}</Badge>
                </div>
                <CardDescription>
                  Aktionen, bei denen du einen QR-Code angefordert hast
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Tabs
                  value={selectedView.key}
                  onValueChange={(v) => {
                    setActionsStatus(v);
                    setActionsPage(1);
                  }}
                >
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="OPEN">Offen ({openActions.length})</TabsTrigger>
                    <TabsTrigger value="EXPIRED">Abgelaufen ({expiredActions.length})</TabsTrigger>
                    <TabsTrigger value="REDEEMED">Eingelöst ({redeemedActions.length})</TabsTrigger>
                  </TabsList>

                  <TabsContent value={selectedView.key} className="mt-4">
                    {selectedView.items.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Keine {selectedView.title.toLowerCase()}en Aktionen.</p>
                    ) : (
                      <>
                        <div>
                          {pagedItems.map(renderActionRow)}
                        </div>

                        <div className="flex items-center justify-between pt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setActionsPage(Math.max(1, safePage - 1))}
                            disabled={safePage <= 1}
                          >
                            Zurück
                          </Button>
                          <div className="text-sm text-muted-foreground">
                            Seite {safePage} / {totalPages}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setActionsPage(Math.min(totalPages, safePage + 1))}
                            disabled={safePage >= totalPages}
                          >
                            Weiter
                          </Button>
                        </div>
                      </>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Active Stamp Cards */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-foreground mb-4">Meine aktiven Stempelkarten</h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : activeCards.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Noch keine aktiven Stempelkarten.</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Aktiviere eine Karte aus den verfügbaren Aktionen unten.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeCards.map((userCard) => {
                const card = userCard.stampCard;
                const progress = userCard.progress || 0;
                const total = card?.stampCount || 10;
                const percentage = Math.round((progress / total) * 100);
                const isCompleted = !!userCard.completedAt;
                const canClaimReward = isCompleted && !userCard.redeemedAt;

                return (
                  <Card
                    key={userCard.id}
                    className={`${isCompleted ? 'border-green-500 border-2' : ''} cursor-pointer hover:shadow-md transition-shadow`}
                    onClick={() => {
                      setSelectedQrCard({ userCardId: userCard.id, stampCardId: userCard?.stampCard?.id || null });
                      setQrPopupStatus(null);
                      setShowQrSuccessAnimation(false);
                      qrPopupPrevRef.current = {
                        progress: Number(userCard?.progress || 0),
                        redeemedAt: userCard?.redeemedAt || null,
                      };
                    }}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{card?.name}</CardTitle>
                          <CardDescription className="mt-1">
                            {card?.company?.name}
                          </CardDescription>
                        </div>
                        {isCompleted && (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="font-medium">
                            {progress} / {total} Stempel
                          </span>
                          <span className="text-muted-foreground">{percentage}%</span>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>

                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm font-medium text-blue-900">
                          Belohnung:
                        </p>
                        <p className="text-sm text-blue-800 mt-1">
                          {card?.reward}
                        </p>
                      </div>

                      <div className="text-xs text-blue-700 flex items-center gap-2">
                        <QrCode className="h-4 w-4" />
                        Tippen, um persönlichen QR groß zu öffnen
                      </div>

                      {isCompleted ? (
                        <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                          <p className="text-sm font-medium text-green-900 flex items-center gap-2">
                            <Award className="h-4 w-4" />
                            Vollständig!
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            {canClaimReward ? 'Belohnung jetzt einfordern' : 'Belohnung bereits eingelöst'}
                          </p>
                          {canClaimReward && (
                            <Button
                              type="button"
                              size="sm"
                              className="mt-2 h-8 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedQrCard({ userCardId: userCard.id, stampCardId: userCard?.stampCard?.id || null });
                                setQrPopupStatus(null);
                                setShowQrSuccessAnimation(false);
                                qrPopupPrevRef.current = {
                                  progress: Number(userCard?.progress || 0),
                                  redeemedAt: userCard?.redeemedAt || null,
                                };
                              }}
                            >
                              Belohnung einfordern
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground">
                          Noch {total - progress} Stempel bis zur Belohnung
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Available Stamp Cards */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-foreground">Verfügbare Stempelkarten</h2>
            <div className="relative w-64">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Suche nach Karten..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : filteredAvailable.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {searchTerm
                    ? 'Keine Stempelkarten gefunden.'
                    : 'Aktuell keine neuen Stempelkarten verfügbar.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAvailable.map((card) => (
                <Card key={card.id}>
                  <CardHeader>
                    <CardTitle className="text-lg">{card.name}</CardTitle>
                    <CardDescription>{card.company?.name}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {card.description && (
                      <p className="text-sm text-muted-foreground">{card.description}</p>
                    )}

                    <div className="flex items-center gap-2 text-sm">
                      <TrendingUp className="h-4 w-4 text-blue-600" />
                      <span>{card.stampCount} Stempel sammeln</span>
                    </div>

                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="text-sm font-medium text-purple-900">
                        Belohnung:
                      </p>
                      <p className="text-sm text-purple-800 mt-1">
                        {card.reward}
                      </p>
                    </div>

                    <Button
                      className="w-full"
                      onClick={() => handleActivateCard(card.id)}
                      disabled={activating === card.id}
                    >
                      {activating === card.id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Aktiviere...
                        </>
                      ) : (
                        'Jetzt aktivieren'
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {progressPopup && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm bg-background border shadow-lg rounded-lg p-4">
          <p className="text-sm font-semibold text-foreground">Stempelkarte aktualisiert</p>
          <p className="text-sm text-foreground mt-1">{progressPopup.name}</p>
          <p className="text-sm text-blue-700 mt-1">
            Fortschritt: {progressPopup.before}/{progressPopup.total} {'->'} {progressPopup.after}/{progressPopup.total}
          </p>
        </div>
      )}

      {selectedQrCard?.userCardId && (() => {
        const selectedCard =
          activeCards.find((x) => x.id === selectedQrCard?.userCardId) ||
          activeCards.find((x) => x?.stampCard?.id === selectedQrCard?.stampCardId) ||
          null;
        if (!selectedCard) return null;
        const code = selectedCard?.personalScanCode || '';
        const expiresAtTs = selectedCard?.personalScanExpiresAt ? new Date(selectedCard.personalScanExpiresAt).getTime() : 0;
        const secondsLeft = Math.max(0, Math.ceil((expiresAtTs - qrNow) / 1000));
        const qrImageUrl = code ? `${qrBase}?data=${encodeURIComponent(code)}&size=420` : null;
        const isRewardClaim = !!selectedCard?.completedAt && !selectedCard?.redeemedAt;
        return (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelectedQrCard(null)}>
            <div className="bg-background rounded shadow w-full max-w-xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold">{isRewardClaim ? 'Belohnung einfordern' : 'Persönlicher Stempel-QR'}</h3>
                <Button type="button" size="sm" variant="outline" onClick={() => setSelectedQrCard(null)}>Schließen</Button>
              </div>

              <div className="text-sm text-foreground">
                {selectedCard?.stampCard?.name || 'Stempelkarte'} · {selectedCard?.stampCard?.company?.name || ''}
              </div>

              {isRewardClaim && (
                <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-900">
                  Diese Karte ist voll. Zeige den QR-Code zum Einlösen deiner Belohnung.
                </div>
              )}

              {showQrSuccessAnimation ? (
                <div className={`space-y-3 transition-opacity duration-500 ${qrAnimFading ? "opacity-0" : "opacity-100"}`}>
                  <div className="flex justify-center">
                    <div className="w-64 h-64">
                      <DotLottieReact
                        src="https://lottie.host/77c4413f-852f-4b5f-80a7-e1863df7fac9/RaxkN1xThE.lottie"
                        autoplay
                        loop={false}
                      />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-base font-semibold text-green-700">Einlösung erfolgreich</p>
                    {qrPopupStatus && typeof qrPopupStatus.before === 'number' && typeof qrPopupStatus.after === 'number' && typeof qrPopupStatus.total === 'number' && (
                      <p className="text-sm text-foreground mt-1">
                        {qrPopupStatus.before}/{qrPopupStatus.total} {'->'} {qrPopupStatus.after}/{qrPopupStatus.total}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {qrImageUrl && (
                    <div className="flex justify-center">
                      <img
                        src={qrImageUrl}
                        alt="Persönlicher Stempel QR"
                        className="w-72 h-72 rounded border bg-background p-2"
                      />
                    </div>
                  )}

                  <div className="border rounded p-3 bg-muted">
                    <div className="text-xs text-muted-foreground">Code zum Abtippen</div>
                    <div className="mt-1 text-xl font-semibold tracking-wider">{code || '-'}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Gültig für {secondsLeft}s · danach automatisch neu generiert
                    </div>
                  </div>

                  {qrPopupStatus && (
                    <div className="border rounded p-3 bg-green-50 text-green-800 text-sm text-center">
                      <div>{qrPopupStatus.message}</div>
                    </div>
                  )}
                </>
              )}

              <p className="text-xs text-muted-foreground">
                Der Code ist kurzlebig (60 Sekunden) und personengebunden.
              </p>
            </div>
          </div>
        );
      })()}

      {selectedActionQr?.code && (() => {
        const qrImageUrl = `${qrBase}?data=${encodeURIComponent(selectedActionQr.code)}&size=420`;
        return (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelectedActionQr(null)}>
            <div className="bg-background rounded shadow w-full max-w-xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold">Mein Aktions-QR</h3>
                <Button type="button" size="sm" variant="outline" onClick={() => setSelectedActionQr(null)}>Schließen</Button>
              </div>

              <div className="text-sm text-foreground">
                {[selectedActionQr.actionTitle, selectedActionQr.companyName || selectedActionQr.campaignName].filter(Boolean).join(' · ')}
              </div>

              <div className="flex justify-center">
                <img
                  src={qrImageUrl}
                  alt="Aktions QR"
                  className="w-72 h-72 rounded border bg-background p-2"
                />
              </div>

              <div className="border rounded p-3 bg-muted">
                <div className="text-xs text-muted-foreground">Code zum Abtippen</div>
                <div className="mt-1 text-xl font-semibold tracking-wider">{selectedActionQr.code}</div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
